<template>
  <div class="totp-setup-page">
    <b-container class="py-5">
      <b-row class="justify-content-center">
        <b-col md="8" lg="6">
          <b-card class="shadow-lg border-0 rounded-4">
            <!-- HEADER -->
            <template #header>
              <div class="text-center py-2">
                <h3 class="mb-0 text-uppercase font-weight-bold text-primary">
                  {{ $t('page.twoFactorAuth.2faModalTitle') }}
                </h3>
              </div>
            </template>

            <!-- BODY -->
            <b-card-body class="px-4 py-4">
              <div class="text-center">
                <!-- Instructions -->
                <div class="mb-4">
                  <p class="mb-3 font-weight-bold label h5">{{ $t('page.twoFactorAuth.2faModalInstructions') }}</p>
                  <div class="instruction-steps bg-light p-3 rounded">
                    <small class="text-muted">
                      {{ $t('page.twoFactorAuth.enable2FaStep1') }}<br>
                      {{ $t('page.twoFactorAuth.enable2FaStep2') }}<br>
                      {{ $t('page.twoFactorAuth.enable2FaStep3') }}
                    </small>
                  </div>
                </div>

                <!-- QR Code -->
                <div class="mb-4">
                  <div v-if="qrCodeDataUrl" class="d-inline-block p-4 border rounded-3 bg-white">
                    <img :src="qrCodeDataUrl" alt="2FA QR Code" class="img-fluid" style="max-width: 220px;">
                  </div>
                  <div v-else-if="isGeneratingQR" class="d-inline-block p-4 border rounded-3 bg-light"
                    style="width: 220px; height: 220px;">
                    <div class="d-flex align-items-center justify-content-center h-100">
                      <b-icon icon="arrow-clockwise" animation="spin-pulse" font-scale="4" variant="primary"></b-icon>
                    </div>
                  </div>
                  <div v-else-if="!hasValidTotpData" class="d-inline-block p-4 border rounded-3 text-muted bg-light"
                    style="width: 220px; height: 220px;">
                    <div class="d-flex align-items-center justify-content-center h-100">
                      <small>{{ $t('page.twoFactorAuth.2faModalWaitingForData') }}</small>
                    </div>
                  </div>
                  <div v-else class="d-inline-block p-4 border rounded-3 text-danger bg-light"
                    style="width: 220px; height: 220px;">
                    <div class="d-flex align-items-center justify-content-center h-100">
                      <small>{{ $t('page.twoFactorAuth.2faModalQRError') }}</small>
                    </div>
                  </div>
                </div>

                <!-- Manual Entry Option -->
                <div class="mb-4">
                  <b-card class="border-0 bg-light">
                    <details class="p-2">
                      <summary class="text-primary font-weight-bold" style="cursor: pointer;">
                        <small>{{ $t('page.twoFactorAuth.2faModalManualSetup') }}</small>
                      </summary>
                      <div class="mt-3 text-left">
                        <small class="text-muted d-block mb-3">
                          {{ $t("page.twoFactorAuth.manualInstruction") }}
                        </small>
                        <div class="row mb-2">
                          <div class="col-sm-3"><small><strong>Account:</strong></small></div>
                          <div class="col-sm-9"><code class="small">{{ totp?.issuer || 'Foresight' }}</code></div>
                        </div>
                        <div class="row">
                          <div class="col-sm-3"><small><strong>Key:</strong></small></div>
                          <div class="col-sm-9">
                            <code class="user-select-all bg-white p-2 rounded small border">{{ secretKey }}</code>
                          </div>
                        </div>
                      </div>
                    </details>
                    <div v-if="!qrCodeDataUrl && hasValidTotpData" class="mt-3 text-center">
                      <b-button size="sm" variant="outline-primary" @click="generateQRCode">
                        {{ $t('2faModalRetryQR') || 'Retry QR Code' }}
                      </b-button>
                    </div>
                  </b-card>
                </div>

                <!-- Verification Input -->
                <div class="mb-4">
                  <h5 class="font-weight-bold label mb-3">{{ $t('page.twoFactorAuth.2faModalVerificationLabel') }}</h5>
                  <b-form-input v-model="verificationCode"
                    maxlength="6" size="lg"
                    class="text-center mb-2" style="letter-spacing: 0.3em; font-size: 1.2em;" @input="validateCode"
                    @keyup.enter="verifyTOTP">
                  </b-form-input>
                  <small v-if="!totpError" class="text-muted"></small>
                  <small v-else class="text-danger">{{ $t(`${totpError}`) }}</small>
                </div>
              </div>
            </b-card-body>

            <!-- FOOTER -->
            <template #footer>
              <div class="d-flex justify-content-between align-items-center py-2">
                <b-button variant="outline-secondary" class="px-4 py-2 rounded-pill" @click="goBack">
                  {{ $t("button.cancel") || "Cancel" }}
                </b-button>
                <b-button variant="primary" class="px-4 py-2 rounded-pill emotiva-pink-background"
                  style="color: #fff; border: none;" :disabled="isFormInvalid" @click="verifyTOTP">
                  {{ $t("button.proceed") }}
                </b-button>
              </div>
            </template>
          </b-card>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import QRCode from 'qrcode'

export default {
  name: 'TotpSetupPage',
  props: {
    totp: {
      type: Object,
      required: false,
      default: () => ({})
    }
  },
  data() {
    return {
      qrCodeDataUrl: null,
      verificationCode: '',
      isGeneratingQR: false,
      wrongTotpMessage: ''
    }
  },
  computed: {
    isFormInvalid() {
      return !this.verificationCode || this.verificationCode.length !== 6 || !/^\d{6}$/.test(this.verificationCode);
    },
    secretKey() {
      // Handle different possible data structures
      return this.$store.getters['authTotpVerification/getSecret'];
    },
    totpError() {
      return this.wrongTotpMessage || false
    },
    totpUrl() {
      if (!this.totp) return '';

      try {
        // Build the otpauth URL from the TOTP object properties
        const email = this.$store.getters['authTotpVerification/getEmail'] || 'Foresight-user'
        const issuer = 'Foresight';
        const label = email;
        const secret =  this.$store.getters['authTotpVerification/getSecret']
        const algorithm =  'SHA1';
        const digits =  6;
        const period =  30;

        if (!secret) {
          console.error('No secret found in TOTP object');
          return '';
        }

        // Construct the otpauth URL
        const params = new URLSearchParams({
          issuer: issuer,
          secret: secret,
          algorithm: algorithm,
          digits: digits.toString(),
          period: period.toString()
        });

        const url = `otpauth://totp/${encodeURIComponent(issuer)}:${encodeURIComponent(label)}?${params.toString()}`;
        return url;

      } catch (error) {
        console.error('Error building TOTP URL:', error);
        return '';
      }
    },
    hasValidTotpData() {
      const url = this.totpUrl;
      return url && typeof url === 'string' && url.length > 0 && url.startsWith('otpauth://');
    }
  },
  watch: {
    totp: {
      handler(newVal) {
        if (newVal && this.hasValidTotpData) {
          this.generateQRCode();
        }
      },
      deep: true,
      immediate: true
    }
  },
  methods: {


    async generateQRCode() {
      if (!this.hasValidTotpData) {
        return;
      }

      this.isGeneratingQR = true;
      try {
        // Generate QR code as data URL
        this.qrCodeDataUrl = await QRCode.toDataURL(this.totpUrl, {
          errorCorrectionLevel: 'M',
          type: 'image/png',
          quality: 0.92,
          margin: 1,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          },
          width: 220
        });
      } catch (error) {
        console.error('Error generating QR code:', error);
        this.$emit('qr-error', error);
      } finally {
        this.isGeneratingQR = false;
      }
    },

    validateCode() {
      // Remove any non-numeric characters
      this.verificationCode = this.verificationCode.replace(/\D/g, '');

      // Limit to 6 digits
      if (this.verificationCode.length > 6) {
        this.verificationCode = this.verificationCode.substring(0, 6);
      }
    },

    resetForm() {
      this.verificationCode = '';
      this.wrongTotpMessage = '';
      this.qrCodeDataUrl = null;
      this.isGeneratingQR = false;
    },

    goBack() {
      this.$router.push('/login');
    },

    submit() {
      if (this.isFormInvalid) return;

      this.$emit("submit", {
        code: this.verificationCode,
        secret: this.secretKey
      });

      // Reset form
      this.resetForm();
    },

    async verifyTOTP() {
      if (this.isFormInvalid) return;
      let redirectRoute = ''

      this.$store.commit("loader/SET_LOADER", {});

      try {
        const response = await this.$store.dispatch('authTotpVerification/validateUserTotpAndLogin', {
          totp: this.verificationCode,
        });
        this.$store.commit("auth/SET_DATA_AFTER_LOGIN", response)
        await this.$store.dispatch("auth/retrieveClient");

        this.$store.commit("auth/SET_IS_LOGGED_IN", true);

        // set up subscription after login
        this.$store.dispatch("subscription/retrieveActiveSubscriptionByClientId")
          .then((response) => {
            this.activeSubscription = response;
          })
          .catch((e) => console.error('Something went wrong when fetching subscription: ', e))

        // this.$store.commit("settings/SET_TOUR_ACTIVE", this.isFirstLogin);

        redirectRoute = this.$route.query.redirect;
        if (redirectRoute) {
          this.$router.replace(redirectRoute);
        } else {
          this.$router.replace({ name: "Dashboard" });
        }

        // Navigate to success page or dashboard
        this.$router.push('/dashboard'); // Adjust route as needed

      } catch (error) {
        console.error("Error verifying TOTP: ", error);
        this.wrongTotpMessage = 'page.twoFactorAuth.2FAWrongTOPT';
      } finally {
        this.$store.dispatch("loader/close");
      }
    }
  },

  beforeDestroy() {
    // Clean up data URL to prevent memory leaks
    if (this.qrCodeDataUrl) {
      URL.revokeObjectURL(this.qrCodeDataUrl);
    }
  }
}
</script>

<style scoped>
.totp-setup-page {
  min-height: 100vh;
  /* background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%); */
  background-color: var(--emotiva-pink-login);
}

.instruction-steps {
  text-align: left;
  max-width: 400px;
  margin: 0 auto;
}

.label {
  color: var(--emotiva-text-color, #333);
}

.user-select-all {
  user-select: all;
  -webkit-user-select: all;
  -moz-user-select: all;
  -ms-user-select: all;
}

.emotiva-pink-background {
  background-color: var(--emotiva-pink, #e91e63) !important;
}

code {
  word-break: break-all;
}

.rounded-4 {
  border-radius: 1rem !important;
}

.rounded-3 {
  border-radius: 0.75rem !important;
}

.rounded-pill {
  border-radius: 50rem !important;
}

/* Card hover effect */
.card {
  transition: transform 0.2s ease-in-out;
}

.card:hover {
  transform: translateY(-2px);
}

/* Enhanced button styles */
.btn {
  transition: all 0.2s ease-in-out;
  font-weight: 600;
}

.btn:hover {
  transform: translateY(-1px);
}

/* Input focus styles */
.form-control:focus {
  border-color: var(--emotiva-pink, #e91e63);
  box-shadow: 0 0 0 0.2rem rgba(233, 30, 99, 0.25);
}
</style>